import type { ReactNode } from "react";

import { Card } from "@/components/Card";
import { useProfileLabel } from "@/lib/profileLabels";

import { StackedBar } from "./Aggregate";

/**
 * Per-query and per-voter breakdown tables. Both reuse the same
 * BreakdownTable component; only the leading "key" column differs.
 */

export interface PerQueryUiRow {
  queryText: string;
  aBetter: number;
  bBetter: number;
  both_good: number;
  both_bad: number;
  total: number;
}

export interface PerVoterUiRow {
  voterEmail: string;
  aBetter: number;
  bBetter: number;
  both_good: number;
  both_bad: number;
  total: number;
}

export function PerQuerySection({
  rows,
  profileA,
  profileB,
}: {
  rows: PerQueryUiRow[];
  profileA: string;
  profileB: string;
}) {
  return (
    <div style={{ marginBottom: 22 }}>
      <SectionHeader
        title="Per-query breakdown"
        sub="Unique queries with at least one vote on this pair."
        count={rows.length}
      />
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <BreakdownTable
          rows={rows}
          keyLabel="Query"
          keyRender={(r) => (
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{r.queryText}</div>
          )}
          profileA={profileA}
          profileB={profileB}
        />
      </Card>
    </div>
  );
}

export function PerVoterSection({
  rows,
  profileA,
  profileB,
}: {
  rows: PerVoterUiRow[];
  profileA: string;
  profileB: string;
}) {
  return (
    <div>
      <SectionHeader
        title="Per-voter breakdown"
        sub="Votes grouped by email. Useful for spotting systematic bias."
        count={rows.length}
      />
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <BreakdownTable
          rows={rows}
          keyLabel="Voter"
          keyRender={(r) => {
            const handle = r.voterEmail.split("@")[0] ?? r.voterEmail;
            const initials =
              handle
                .split(/[._-]/)
                .filter(Boolean)
                .map((s) => s[0] ?? "")
                .slice(0, 2)
                .join("")
                .toUpperCase() || "?";
            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 99,
                    background: "var(--atf-pink-50, #fdf2f8)",
                    color: "#831843",
                    display: "grid",
                    placeItems: "center",
                    fontSize: 10,
                    fontWeight: 600,
                    fontFamily: "var(--font-sans)",
                  }}
                >
                  {initials}
                </div>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{r.voterEmail}</span>
              </div>
            );
          }}
          profileA={profileA}
          profileB={profileB}
        />
      </Card>
    </div>
  );
}

function SectionHeader({
  title,
  sub,
  count,
}: {
  title: string;
  sub: string;
  count: number;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: 10,
        marginBottom: 10,
      }}
    >
      <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{title}</h2>
      <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>{sub}</span>
      <span
        style={{
          marginLeft: "auto",
          fontSize: 11,
          color: "var(--muted-foreground)",
          fontFamily: "var(--font-mono)",
        }}
      >
        {count} rows
      </span>
    </div>
  );
}

interface BreakdownRow {
  aBetter: number;
  bBetter: number;
  both_good: number;
  both_bad: number;
  total: number;
}

function BreakdownTable<R extends BreakdownRow>({
  rows,
  keyLabel,
  keyRender,
  profileA,
  profileB,
}: {
  rows: R[];
  keyLabel: string;
  keyRender: (r: R) => ReactNode;
  profileA: string;
  profileB: string;
}) {
  const label = useProfileLabel();
  const cols: Array<{ label: string; align: "left" | "right" }> = [
    { label: keyLabel, align: "left" },
    { label: `${label(profileA)} wins`, align: "right" },
    { label: `${label(profileB)} wins`, align: "right" },
    { label: "Both good", align: "right" },
    { label: "Both bad", align: "right" },
    { label: "Distribution", align: "left" },
    { label: "Total", align: "right" },
  ];
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr
          style={{
            borderBottom: "1px solid var(--border)",
            background: "var(--secondary)",
          }}
        >
          {cols.map((c) => (
            <th
              key={c.label}
              style={{
                padding: "10px 14px",
                textAlign: c.align,
                fontWeight: 600,
                fontSize: 11,
                color: "var(--muted-foreground)",
                fontFamily: "var(--font-mono)",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr
            key={i}
            style={{
              borderBottom: i === rows.length - 1 ? "none" : "1px solid var(--border)",
            }}
          >
            <td style={{ padding: "12px 14px" }}>{keyRender(r)}</td>
            <td
              style={{
                padding: "12px 14px",
                textAlign: "right",
                fontFamily: "var(--font-mono)",
                color: r.aBetter > r.bBetter ? "var(--atf-pink-700)" : "var(--foreground)",
                fontWeight: r.aBetter > r.bBetter ? 600 : 500,
              }}
            >
              {r.aBetter}
            </td>
            <td
              style={{
                padding: "12px 14px",
                textAlign: "right",
                fontFamily: "var(--font-mono)",
                color: r.bBetter > r.aBetter ? "var(--atf-blue-950)" : "var(--foreground)",
                fontWeight: r.bBetter > r.aBetter ? 600 : 500,
              }}
            >
              {r.bBetter}
            </td>
            <td
              style={{
                padding: "12px 14px",
                textAlign: "right",
                fontFamily: "var(--font-mono)",
                color: "var(--muted-foreground)",
              }}
            >
              {r.both_good}
            </td>
            <td
              style={{
                padding: "12px 14px",
                textAlign: "right",
                fontFamily: "var(--font-mono)",
                color: "var(--muted-foreground)",
              }}
            >
              {r.both_bad}
            </td>
            <td style={{ padding: "12px 14px", width: 200 }}>
              <StackedBar
                a={r.aBetter}
                b={r.bBetter}
                bg={r.both_good}
                bb={r.both_bad}
                total={r.total}
              />
            </td>
            <td
              style={{
                padding: "12px 14px",
                textAlign: "right",
                fontFamily: "var(--font-mono)",
              }}
            >
              {r.total}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
